// InviteFriends.js
import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { Icon } from '@iconify/react';
import { RichCoinIcon } from '../assets/icons/Icons';
import { TelegramUserContext } from '../App';
import { useUserData } from '../contexts/UserDataContext';
import { buildReferralUrl, APP_CONFIG } from '../config/app';

// ----------------------------
// CONFIGURATION
// ----------------------------

const SOCIAL_ICONS = [
  {
    id: 'telegram',
    name: 'Telegram',
    icon: 'hugeicons:telegram',
    bg: '#26A5E4'
  },
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    icon: 'ic:outline-whatsapp',
    bg: '#25D366'
  },
  {
    id: 'messenger',
    name: 'Messenger',
    icon: 'ri:messenger-fill',
    bg: 'linear-gradient(135deg, #00C6FF 0%, #7D5FFF 50%, #FF2D95 100%)'
  },
  {
    id: 'discord',
    name: 'Discord',
    icon: 'skill-icons:discord',
    bg: '#5865F2'
  },
  {
    id: 'x',
    name: 'X (Twitter)',
    icon: 'prime:twitter',
    bg: '#000000'
  },
  {
    id: 'link',
    name: 'Link',
    icon: 'fluent:link-24-regular',
    bg: '#718096'
  }
];

const InviteContainer = styled.div`
  margin: 8px;
  padding: 0 4px;
`;

const InviteHeader = styled.div`
  margin-bottom: 8px;
  text-align: left;
`;

const InviteReward = styled.p`
  font-size: 13px;
  margin: 0;
`;

const SocialIconsContainer = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 8px;
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
`;

const SocialIcon = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, opacity 0.2s ease;

  svg {
    width: 24px;
    height: 24px;
    color: white;
  }

  &:hover {
    opacity: 0.8;
    transform: scale(0.95);
  }
  
  &:active {
    transform: scale(0.9);
  }
`;

const InviteActionContainer = styled.div`
  background-color: white;
  border-radius: 12px;
  padding: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
`;

const InviteInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InviteText = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
`;

const InviteTitle = styled.div`
  font-weight: 500;
  font-size: 13px;
`;

const InviteBonus = styled.div`
  color: #757575;
  font-size: 12px;
`;

const GoButton = styled.button`
  background-color: black;
  color: white;
  border: none;
  border-radius: 16px;
  padding: 6px 20px;
  font-weight: bold;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    transform: scale(0.95);
  }

  &.copying {
    background-color: #4CAF50;
  }
`;

const CopySuccess = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: #4CAF50;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 500;
  z-index: 1000;
  animation: fadeInOut 2s ease-in-out;

  @keyframes fadeInOut {
    0%, 100% { opacity: 0; }
    20%, 80% { opacity: 1; }
  }
`;

const InviteFriends = () => {
  const telegramUser = useContext(TelegramUserContext);
  const { userData } = useUserData(); // Only get userData, no need for loading functions
  const [isCopying, setIsCopying] = useState(false);
  const [showCopySuccess, setShowCopySuccess] = useState(false);

  // No need to load data here - HomePage already handles all data loading

  // Handle copying referral link
  const handleCopyReferralLink = async () => {
    const referralCode = userData?.referral_code || telegramUser?.id;
    
    if (!referralCode) {
      console.error('No referral code available');
      alert('Referral code not available yet. Please wait a moment and try again.');
      return;
    }

    setIsCopying(true);
    
    try {
      const referralUrl = buildReferralUrl(referralCode);
      
      // Check if clipboard API is available
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(referralUrl);
      } else {
        // Fallback method for non-secure contexts or older browsers
        const textArea = document.createElement('textarea');
        textArea.value = referralUrl;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (!successful) {
          throw new Error('Copy command failed');
        }
      }
      
      setShowCopySuccess(true);
      setTimeout(() => setShowCopySuccess(false), 2000);
      
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      
      // Show the URL to user if copying fails
      const referralUrl = buildReferralUrl(referralCode);
      if (window.Telegram && window.Telegram.WebApp) {
        // In Telegram, show alert with the URL
        window.Telegram.WebApp.showAlert(`Copy this link manually:\n\n${referralUrl}`);
      } else {
        // In browser, use prompt so user can manually copy
        prompt('Copy this referral link:', referralUrl);
      }
    }
    
    setTimeout(() => setIsCopying(false), 500);
  };

  // Handle social sharing
  const handleSocialShare = (platform) => {
    const referralCode = userData?.referral_code || telegramUser?.id;
    if (!referralCode) return;

    const referralUrl = buildReferralUrl(referralCode);
    const shareText = `Join me in ${APP_CONFIG.APP_NAME} and earn ${APP_CONFIG.REFERRAL_REWARD} ${APP_CONFIG.CURRENCY_SYMBOL}!`;

    switch (platform) {
      case 'telegram':
        window.open(`https://t.me/share/url?url=${encodeURIComponent(referralUrl)}&text=${encodeURIComponent(shareText)}`, '_blank');
        break;
      case 'whatsapp':
        // Direct redirect without popup - works better on mobile and desktop
        window.location.href = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + referralUrl)}`;
        break;
      case 'messenger':
        // Direct redirect to Facebook Messenger sharing
        window.location.href = `https://www.facebook.com/dialog/send?link=${encodeURIComponent(referralUrl)}&app_id=your_app_id&redirect_uri=${encodeURIComponent(window.location.href)}`;
        break;
      case 'discord':
        // Copy to clipboard for Discord sharing (Discord doesn't have direct URL sharing)
        const discordMessage = `${shareText} ${referralUrl}`;
        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(discordMessage);
        } else {
          // Fallback for non-secure contexts
          const textArea = document.createElement('textarea');
          textArea.value = discordMessage;
          textArea.style.position = 'fixed';
          textArea.style.opacity = '0';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }
        // Show Discord-specific feedback
        if (window.Telegram && window.Telegram.WebApp) {
          window.Telegram.WebApp.showAlert('Message copied! Now open Discord and paste it in your server or DM.');
        } else {
          alert('Message copied! Now open Discord and paste it in your server or DM.');
        }
        break;
      case 'x':
        // Direct redirect to X (Twitter) without popup
        window.location.href = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(referralUrl)}`;
        break;
      case 'link':
        handleCopyReferralLink();
        break;
      default:
        handleCopyReferralLink();
    }
  };
  return (
    <InviteContainer>
      <InviteHeader>
        <InviteReward>+{APP_CONFIG.REFERRAL_REWARD} {APP_CONFIG.CURRENCY_SYMBOL} for each friend you invite</InviteReward>
      </InviteHeader>
      
      <SocialIconsContainer>
        {SOCIAL_ICONS.map(({ id, name, icon, bg }) => (
          <SocialIcon 
            key={id} 
            title={name} 
            style={{ background: bg }}
            onClick={() => handleSocialShare(id)}
          >
            <Icon icon={icon} color="#fff" width="100%" height="100%" />
          </SocialIcon>
        ))}
      </SocialIconsContainer>
      
      <InviteActionContainer>
        <InviteInfo>
          <RichCoinIcon />
          <InviteText>
            <InviteTitle>Copy Referral Link</InviteTitle>
            <InviteBonus>
              {(userData?.referral_code || telegramUser?.id)
                ? `Share and earn ${APP_CONFIG.CURRENCY_SYMBOL}!` 
                : 'Loading referral code...'}
            </InviteBonus>
          </InviteText>
        </InviteInfo>
        <GoButton 
          className={isCopying ? 'copying' : ''}
          onClick={handleCopyReferralLink}
          disabled={!userData?.referral_code && !telegramUser?.id}
          style={{
            opacity: (userData?.referral_code || telegramUser?.id) ? 1 : 0.5,
            cursor: (userData?.referral_code || telegramUser?.id) ? 'pointer' : 'not-allowed'
          }}
        >
          {isCopying ? 'COPIED!' : 'COPY'}
        </GoButton>
      </InviteActionContainer>

      {showCopySuccess && (
        <CopySuccess>
          Referral link copied to clipboard!
        </CopySuccess>
      )}
    </InviteContainer>
  );
};

export default InviteFriends;
